import { Directive, effect, ElementRef, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { DragAndDropService } from '@features/drag-and-drop/drag-and-drop.service';
import { IDraggable, IDropTarget } from '@features/drag-and-drop/drag-and-drop.types';
import { add, multiply } from '@shared/vector.utils';
import { Vector2ElementNormalized, Vector2Screen } from '@typings/vectors';

@Directive({
  selector: '[appDropTarget]',
})
export class DropTargetDirective implements IDropTarget, OnInit, OnDestroy {
  readonly #dragAndDropService = inject(DragAndDropService);
  readonly el = inject<ElementRef<HTMLElement>>(ElementRef);

  // INPUTS
  public dropPivot = input<Vector2ElementNormalized>([0.5, 0.5] as Vector2ElementNormalized);
  public dropEnabled = input<boolean>(true);

  // OUTPUTS
  draggableAdded = output<IDraggable>();
  draggableRemoved = output<IDraggable>();

  ngOnInit(): void {
    if (this.dropEnabled()) {
      this.#dragAndDropService.registerDropTarget(this);
    } else {
      effect(() => {
        if (this.dropEnabled()) {
          this.#dragAndDropService.registerDropTarget(this);
        } else {
          this.#dragAndDropService.unregisterDropTarget(this);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.#dragAndDropService.unregisterDropTarget(this);
  }

  public getRect(): { pos: Vector2Screen; size: Vector2Screen } {
    const rect = this.el.nativeElement.getBoundingClientRect();
    return {
      pos: [rect.x, rect.y] as Vector2Screen,
      size: [rect.width, rect.height] as Vector2Screen,
    };
  }

  public getPivot(): Vector2Screen {
    const rect = this.getRect();
    return add(rect.pos, multiply(rect.size, this.dropPivot())) as Vector2Screen;
  }

  public added(draggable: IDraggable): void {
    this.draggableAdded.emit(draggable);
  }

  public removed(draggable: IDraggable): void {
    this.draggableRemoved.emit(draggable);
  }
}
